import React, { Dispatch, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  ThemeProvider,
  Typography,
} from '@mui/material'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import SwitchText from '../../components/switch'
import { AppState } from '../../config/types'
import {
  ProductAcabados,
  ProductAcabadosPeraltesPerfil,
  ProductContent,
  ProductDialogText,
  ProductSelected,
  PuestaTierraSelectValue,
} from '../../config/types/contentTypes'
import { DispatchAction } from '../../config/types/contextTypes'
import { addToMyList, updateMyList } from '../../context/actions/myListActions'
import { getProductById } from '../../context/actions/productActions'
import {
  DEFAULT_SELECT_ITEM,
  updateProductSelected,
} from '../../context/actions/productSelectedActions'
import { useStateValue } from '../../context/store'
import withRouter from '../../hooks/UseWithRouter'
import { themeCharofil } from '../../styles/styles'
import Loading from '../loading'
import Content from './content'
import { useStyles } from './useStyles'

interface Props {}

const Product: React.FC<Props> = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const location = useLocation()

  const defaultDialogTexts: ProductDialogText = {
    title: 'Añadir a la lista',
    description: '',
    cancel: 'Cancelar',
    agree: 'Aceptar',
  }
  const errorDialogTexts: ProductDialogText = {
    title: 'Añadir a la lista',
    description: '',
    cancel: '',
    agree: 'Aceptar',
  }

  const [searchParams] = useSearchParams()
  const { state, dispatch }: { state: AppState; dispatch: Dispatch<DispatchAction> } =
    useStateValue()

  const myList: ProductSelected[] | undefined = state?.myList
  const productContent: ProductContent | undefined = state?.product
  const productSelected: ProductSelected | undefined = state?.productSelected
  const [productRelated, setProductRelated] = useState<number>(
    productSelected?.id ? productSelected.id : 0,
  )
  const indexEdit: number | undefined = location?.state?.indexEdit

  const [open, setOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [dialogText, setDialogText] = useState<ProductDialogText>(defaultDialogTexts)

  useEffect(() => {
    async function getProductSelected(productId: number) {
      if (productId && !hasFetched) {
        await getProductById(dispatch, productId)
        setHasFetched(true)
        setIsLoading(false)
      } else {
        navigate('/')
      }
    }

    const productId = searchParams.get('id')
    if (
      !isLoading &&
      productId &&
      (!productSelected || productSelected?.id !== parseInt(productId))
    ) {
      setIsLoading(true)
      getProductSelected(parseInt(productId))
    }
  }, [dispatch, navigate, isLoading, productSelected, searchParams, hasFetched])

  const getBimetalicoType = () => {
    return productSelected?.acabadoRelated?.name
      ? productSelected?.acabadoRelated?.name.split(' ')[0].trim()
      : productSelected?.acabado?.relatedIds
        ? productSelected?.acabado?.relatedIds.split(';')[0].split(':')[0]
        : 'EZ'
  }

  const getPerfiles = () => {
    if (!productContent) return []

    const perfiles: ProductAcabadosPeraltesPerfil[] = []

    productContent.acabados?.forEach((acabado) => {
      acabado.peraltes?.forEach((peralte) => {
        if (peralte.perfil) {
          const found = perfiles.find((perfil) => perfil.id === peralte.perfil?.id)
          if (!found) {
            perfiles.push(peralte.perfil)
          }
        }
      })
    })

    return perfiles
  }

  const getAcabadosEscalera = (newProductSelected: ProductSelected | undefined) => {
    if (!productContent) return []

    const acabados: ProductAcabados[] = []
    if (!newProductSelected) {
      productContent.acabados?.forEach((acabado) => {
        acabado.peraltes?.forEach((peralte) => {
          if (peralte.perfil?.id === productSelected?.perfilSelect) {
            const found = acabados.find((item) => item.id === acabado.id)
            if (!found) {
              acabados.push(acabado)
            }
          }
        })
      })
    } else {
      newProductSelected.acabados?.forEach((acabado) => {
        acabado.peraltes?.forEach((peralte) => {
          if (peralte.perfil?.id === newProductSelected?.perfilSelect) {
            const found = acabados.find((item) => item.id === acabado.id)
            if (!found) {
              acabados.push(acabado)
            }
          }
        })
      })
    }

    return acabados
  }

  const getLongitudes = (newProductSelected: ProductSelected | undefined) => {
    if (!productSelected || !productSelected.acabado) return []

    const longitudes: string[] = []
    if (!newProductSelected) {
      productSelected?.acabado.peraltes?.forEach((peralte) => {
        if (
          peralte.longitud &&
          !longitudes.includes(peralte.longitud) &&
          peralte.perfil?.id === productSelected?.perfilSelect
        ) {
          longitudes.push(peralte.longitud)
        }
      })
    } else {
      newProductSelected?.acabado?.peraltes?.forEach((peralte) => {
        if (
          peralte.longitud &&
          !longitudes.includes(peralte.longitud) &&
          peralte.perfil?.id === newProductSelected?.perfilSelect
        ) {
          longitudes.push(peralte.longitud)
        }
      })
    }
    return longitudes
  }

  const getPeldaños = (newProductSelected: ProductSelected | undefined) => {
    if (!productSelected || !productSelected.acabado) return []

    const peldaños: string[] = []
    if (!newProductSelected) {
      productSelected?.acabado.peraltes?.forEach((peralte) => {
        if (
          peralte.tipoPeldaño &&
          !peldaños.includes(peralte.tipoPeldaño) &&
          peralte.perfil?.id === productSelected?.perfilSelect &&
          peralte.longitud === productSelected?.longitudSelect
        ) {
          peldaños.push(peralte.tipoPeldaño)
        }
      })
    } else {
      newProductSelected?.acabado?.peraltes?.forEach((peralte) => {
        if (
          peralte.tipoPeldaño &&
          !peldaños.includes(peralte.tipoPeldaño) &&
          peralte.perfil?.id === newProductSelected?.perfilSelect &&
          peralte.longitud === newProductSelected?.longitudSelect
        ) {
          peldaños.push(peralte.tipoPeldaño)
        }
      })
    }

    return peldaños
  }

  const handleAcabadoChange = (
    acabadoId: number | undefined,
    bimetalicoTypeValue: string | undefined,
  ) => {
    if (!acabadoId && !productSelected?.acabado?.id) return
    else if (!acabadoId && productSelected?.acabado?.id) {
      acabadoId = productSelected.acabado.id
    }

    productSelected?.acabados?.forEach((itemAcabado: ProductAcabados) => {
      if (itemAcabado.id === acabadoId) {
        const data: ProductSelected = {
          ...productSelected,
          acabado: itemAcabado,
          peralte: DEFAULT_SELECT_ITEM,
          ancho: DEFAULT_SELECT_ITEM,
          perfilSelect: 0,
          anchoSelect: 0,
          metros: 0,
          union: DEFAULT_SELECT_ITEM,
          soporte: DEFAULT_SELECT_ITEM,
          uniones: [],
          soportes: [],
          derivaciones: new Map<number, number>(),
          puestaTierraSelect: new Map<number, PuestaTierraSelectValue>(),
          otrosAccesorios: new Map<number, number>(),
        }

        if (itemAcabado.acabadosRelated) {
          const acabadoRelated = bimetalicoTypeValue
            ? data.acabado?.acabadosRelated.find((p) => p.name === bimetalicoTypeValue)
            : data.acabado?.acabadosRelated[0]

          if (acabadoRelated) {
            data.acabadoRelated = acabadoRelated
          }
        } else {
          data.acabadoRelated = undefined
        }

        updateProductSelected(dispatch, data)
        return
      }
    })
  }

  const handlePerfilChange = async (event: SelectChangeEvent) => {
    if (!productSelected) return

    const newProductSelected: ProductSelected = {
      ...productSelected,

      perfilSelect: parseInt(event.target.value),
      // Oths
      peralte: DEFAULT_SELECT_ITEM,
      ancho: DEFAULT_SELECT_ITEM,
      anchoSelect: 0,
      pasoSelect: 0,
      union: DEFAULT_SELECT_ITEM,
      soporte: DEFAULT_SELECT_ITEM,
      uniones: [],
      soportes: [],
      derivaciones: new Map<number, number>(),
      puestaTierraSelect: new Map<number, PuestaTierraSelectValue>(),
      otrosAccesorios: new Map<number, number>(),
    }

    newProductSelected.acabado = getAcabadosEscalera(newProductSelected)[0]
    newProductSelected.longitudSelect = getLongitudes(newProductSelected)[0]
    newProductSelected.peldañoSelect = getPeldaños(newProductSelected)[0]

    await updateProductSelected(dispatch, newProductSelected)
  }

  const handleAcabadoEscaleraChange = (acabadoId: number | undefined) => {
    if (!acabadoId && !productSelected?.acabado?.id) return
    else if (!acabadoId && productSelected?.acabado?.id) {
      acabadoId = productSelected.acabado.id
    }

    productSelected?.acabados?.forEach((itemAcabado: ProductAcabados) => {
      if (itemAcabado.id === acabadoId) {
        itemAcabado.peraltes?.forEach((peralte) => {
          if (peralte.perfil?.id === productSelected?.perfilSelect) {
            const found = productSelected?.acabados?.find((item) => item.id === itemAcabado.id)
            if (found) {
              const data: ProductSelected = {
                ...productSelected,
                acabado: itemAcabado,
                // Oths
                peralte: DEFAULT_SELECT_ITEM,
                anchoSelect: 0,
                ancho: DEFAULT_SELECT_ITEM,
                pasoSelect: 0,
                union: DEFAULT_SELECT_ITEM,
                soporte: DEFAULT_SELECT_ITEM,
                uniones: [],
                soportes: [],
                derivaciones: new Map<number, number>(),
                puestaTierraSelect: new Map<number, PuestaTierraSelectValue>(),
                otrosAccesorios: new Map<number, number>(),
              }

              data.longitudSelect = getLongitudes(data)[0]
              data.peldañoSelect = getPeldaños(data)[0]

              updateProductSelected(dispatch, data)
              return
            }
          }
        })
      }
    })
  }

  const handleLongitudChange = (longitud: string) => {
    if (!productSelected) return

    const data: ProductSelected = {
      ...productSelected,
      longitudSelect: longitud,
      // Oths
      peralte: DEFAULT_SELECT_ITEM,
      anchoSelect: 0,
      ancho: DEFAULT_SELECT_ITEM,
      pasoSelect: 0,
      union: DEFAULT_SELECT_ITEM,
      soporte: DEFAULT_SELECT_ITEM,
      uniones: [],
      soportes: [],
      derivaciones: new Map<number, number>(),
      puestaTierraSelect: new Map<number, PuestaTierraSelectValue>(),
      otrosAccesorios: new Map<number, number>(),
    }

    data.peldañoSelect = getPeldaños(data)[0]

    updateProductSelected(dispatch, data)
  }

  const handlePeldañoChange = (peldaño: string) => {
    if (!productSelected) return

    const data: ProductSelected = {
      ...productSelected,
      peldañoSelect: peldaño,
      // Oths
      peralte: DEFAULT_SELECT_ITEM,
      anchoSelect: 0,
      ancho: DEFAULT_SELECT_ITEM,
      pasoSelect: 0,
      union: DEFAULT_SELECT_ITEM,
      soporte: DEFAULT_SELECT_ITEM,
      uniones: [],
      soportes: [],
      derivaciones: new Map<number, number>(),
      puestaTierraSelect: new Map<number, PuestaTierraSelectValue>(),
      otrosAccesorios: new Map<number, number>(),
    }

    updateProductSelected(dispatch, data)
  }

  const displayAcabadosLabel = () => {
    let label = 'Acabados:'
    productSelected?.tabs?.forEach((tipo) => {
      if (tipo.TipoProductoID === 1002) {
        label = 'Calibre:'
        return
      }
    })
    return label
  }

  const handleOpenAddToList = () => {
    let dialogT: ProductDialogText = defaultDialogTexts
    if (productSelected?.peralte?.id === DEFAULT_SELECT_ITEM.id)
      dialogT = {
        ...errorDialogTexts,
        description: `No es posible ${
          isMyListUpdate(indexEdit, myList) ? 'actualizar' : 'añadir a'
        } la lista debido a que no se ha seleccionado un peralte`,
      }
    else if (productSelected?.ancho?.id === DEFAULT_SELECT_ITEM.id)
      dialogT = {
        ...errorDialogTexts,
        description: `No es posible ${
          isMyListUpdate(indexEdit, myList) ? 'actualizar' : 'añadir a'
        } la lista debido a que no se ha seleccionado un ancho`,
      }
    else if (productSelected?.metros === undefined || productSelected?.metros === 0)
      dialogT = {
        ...errorDialogTexts,
        description: `No es posible ${
          isMyListUpdate(indexEdit, myList) ? 'actualizar' : 'añadir a'
        } la lista debido a que no ha ingresado los metros requeridos`,
      }
    else
      dialogT = {
        ...defaultDialogTexts,
        description: `Se ${
          isMyListUpdate(indexEdit, myList) ? 'actualizarán' : 'añadirán'
        } todos los productos seleccionados a su lista`,
      }

    setDialogText(dialogT)
    setOpen(true)
  }

  const handleCancelAddToList = () => {
    setOpen(false)
  }

  const handleAgreeAddToList = async () => {
    if (!productSelected || !productContent) return

    if (isMyListUpdate(indexEdit, myList)) await updateList()
    else await addToList()

    setOpen(false)
  }

  const addToList = async () => {
    if (!productSelected) return

    let continueAdd = true
    // Producto
    if (productSelected.peralte?.id === DEFAULT_SELECT_ITEM.id) continueAdd = false
    else if (productSelected.ancho?.id === DEFAULT_SELECT_ITEM.id) continueAdd = false
    else if (!productSelected.metros) continueAdd = false

    if (!continueAdd) {
      setOpen(false)
      return
    }

    const currentList: ProductSelected[] = myList ? myList : []
    const selectedItems: ProductSelected[] = [
      ...currentList,
      {
        id: productSelected.id,
        nombre: productSelected.nombre,
        descripcion: productSelected.descripcion,
        img: productSelected.img,
        acabado: productSelected.acabado,
        peralte: productSelected.peralte,
        ancho: productSelected.ancho,
        metros: productSelected.metros,
        uniones: productSelected.uniones,
        soportes: productSelected.soportes,
        perfilSelect: productSelected.perfilSelect,
        longitudSelect: productSelected.longitudSelect,
        peldañoSelect: productSelected.peldañoSelect,
        pasoSelect: productSelected.pasoSelect,
        anchoRelated: productSelected.anchoRelated,
        acabadoRelated: productSelected.acabadoRelated,
        peralteRelated: productSelected.peralteRelated,
        derivaciones: productSelected.derivaciones,
        puestaTierraSelect: productSelected.puestaTierraSelect,
        otrosAccesorios: productSelected.otrosAccesorios,
      },
    ]

    await addToMyList(dispatch, selectedItems)
    navigate({ pathname: '/myList' })
  }

  const updateList = async () => {
    if (!productSelected || !productContent || !myList || indexEdit === undefined) return

    let continueAdd = true
    // Producto
    if (productSelected.peralte?.id === DEFAULT_SELECT_ITEM.id) continueAdd = false
    else if (productSelected.ancho?.id === DEFAULT_SELECT_ITEM.id) continueAdd = false
    else if (!productSelected.metros) continueAdd = false

    if (!continueAdd) {
      setOpen(false)
      return
    }

    const currentList: ProductSelected[] = myList ? myList : []
    const selectedItems: ProductSelected = {
      id: productSelected.id,
      nombre: productSelected.nombre,
      descripcion: productSelected.descripcion,
      img: productSelected.img,
      acabado: productSelected.acabado,
      acabadoRelated: productSelected.acabadoRelated,
      peralte: productSelected.peralte,
      peralteRelated: productSelected.peralteRelated,
      ancho: productSelected.ancho,
      anchoRelated: productSelected.anchoRelated,
      metros: productSelected.metros,
      uniones: productSelected.uniones,
      soportes: productSelected.soportes,
      perfilSelect: productSelected.perfilSelect,
      longitudSelect: productSelected.longitudSelect,
      peldañoSelect: productSelected.peldañoSelect,
      pasoSelect: productSelected.pasoSelect,
      derivaciones: productSelected.derivaciones,
      puestaTierraSelect: productSelected.puestaTierraSelect,
      otrosAccesorios: productSelected.otrosAccesorios,
    }

    Object.assign(currentList[indexEdit], selectedItems)
    await updateMyList(dispatch, currentList)
    navigate({ pathname: '/myList' })
  }

  const isMyListUpdate = (indexEdit: number | undefined, myList: ProductSelected[] | undefined) => {
    return indexEdit !== undefined && myList && myList.length
  }

  const getImgFromPerfil = () => {
    let image = productSelected?.acabado?.img
    let alt = productSelected?.acabado?.name

    if (productSelected?.perfilSelect) {
      productSelected?.acabado?.peraltes?.forEach((peralte) => {
        if (peralte.perfil?.id === productSelected?.perfilSelect && peralte.anchos) {
          image = peralte.anchos[0]?.img
        }
      })
    }

    return <img src={image} alt={alt} />
  }

  return productSelected ? (
    <Grid
      container
      direction="column"
      className={classes.cardContainer}
      style={{ minHeight: '95vh', backgroundColor: '#f1f1f1' }}
    >
      <Grid item xs={12} md={8} className={classes.cardContainer}>
        <Card className={classes.root}>
          <CardHeader
            avatar={
              <Avatar aria-label="recipe" className={classes.avatar}>
                <img src={productSelected.img} alt={productSelected.nombre} />
              </Avatar>
            }
            title={productSelected.nombre}
            titleTypographyProps={{
              variant: 'h5',
              fontWeight: 'bold',
              textTransform: 'capitalize',
            }}
            subheader=""
          />

          <CardMedia className={classes.media}>{getImgFromPerfil()}</CardMedia>
          <CardContent>
            <div className={classes.selectContainer}>
              <Grid container direction="column" className={classes.cardContainer}>
                {productSelected.productsRelated?.length ? (
                  <Grid item xs={12} className={classes.fullWidth}>
                    <ThemeProvider theme={themeCharofil}>
                      <Select
                        fullWidth
                        id="product-productsRelated-select"
                        key="product-productsRelated-select"
                        value={productRelated}
                        onChange={(event) => {
                          setProductRelated(parseInt(event.target.value as string))
                          navigate(`/product?id=${event.target.value}`)
                        }}
                      >
                        <MenuItem
                          key={`product-prodRelated-${productSelected.id}`}
                          value={productSelected.id}
                        >
                          {productSelected.nombre}
                        </MenuItem>
                        {productSelected.productsRelated?.map((prodRelated) => (
                          <MenuItem
                            key={`product-prodRelated-${prodRelated.id}`}
                            value={prodRelated.id}
                          >
                            {prodRelated.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                    </ThemeProvider>
                  </Grid>
                ) : null}
                {/* Escalera */}
                {productSelected.id === 1005 &&
                productSelected.perfil &&
                productSelected.acabado?.peraltes ? (
                  <>
                    <Grid item xs={12} className={classes.fullWidth}>
                      <FormControl fullWidth>
                        <ThemeProvider theme={themeCharofil}>
                          <InputLabel id="perfil-select-label">{'Perfil'}</InputLabel>
                          <Select
                            fullWidth
                            labelId="perfil-select-label"
                            id="perfil-select"
                            key="perfil-select"
                            label={'Perfil'}
                            value={`${
                              productSelected.perfilSelect
                                ? `${productSelected.perfilSelect}`
                                : getPerfiles()[0].id
                            }`}
                            onChange={handlePerfilChange}
                          >
                            {productSelected.acabado
                              ? productSelected.acabado.peraltes
                                ? getPerfiles().map((perfil) => (
                                    <MenuItem key={`perfil-${perfil.id}`} value={perfil.id}>
                                      {perfil.nombre}
                                    </MenuItem>
                                  ))
                                : ''
                              : ''}
                          </Select>
                        </ThemeProvider>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} className={classes.fullWidth}>
                      <FormControl fullWidth>
                        <ThemeProvider theme={themeCharofil}>
                          <InputLabel id="acabado-select-label">
                            {displayAcabadosLabel()}
                          </InputLabel>
                          <Select
                            fullWidth
                            labelId="acabado-select-label"
                            id="acabado-select"
                            key="acabado-select"
                            label={displayAcabadosLabel()}
                            value={productSelected.acabado ? `${productSelected.acabado.id}` : '0'}
                            onChange={(event: SelectChangeEvent) => {
                              handleAcabadoEscaleraChange(parseInt(event.target.value))
                            }}
                          >
                            {getAcabadosEscalera(undefined).map((acabado) => (
                              <MenuItem key={`acabado-${acabado.id}`} value={acabado.id}>
                                {acabado.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </ThemeProvider>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} className={classes.fullWidth}>
                      <FormControl fullWidth>
                        <ThemeProvider theme={themeCharofil}>
                          <InputLabel id="longitud-select-label">{'Longitud'}</InputLabel>
                          <Select
                            fullWidth
                            labelId="longitud-select-label"
                            id="longitud-select"
                            key="longitud-select"
                            label={'Longitud'}
                            value={
                              productSelected.longitudSelect
                                ? `${productSelected.longitudSelect}`
                                : '0'
                            }
                            onChange={(event: SelectChangeEvent) => {
                              handleLongitudChange(event.target.value as string)
                            }}
                          >
                            {getLongitudes(undefined).map((longitud) => (
                              <MenuItem key={`longitud-${longitud}`} value={longitud}>
                                {longitud}
                              </MenuItem>
                            ))}
                          </Select>
                        </ThemeProvider>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} className={classes.fullWidth}>
                      <FormControl fullWidth>
                        <ThemeProvider theme={themeCharofil}>
                          <InputLabel id="peldaño-select-label">{'Peldaño'}</InputLabel>
                          <Select
                            fullWidth
                            labelId="peldaño-select-label"
                            id="peldaño-select"
                            key="peldaño-select"
                            label={'Peldaño'}
                            value={
                              productSelected.peldañoSelect
                                ? `${productSelected.peldañoSelect}`
                                : '0'
                            }
                            onChange={(event: SelectChangeEvent) => {
                              handlePeldañoChange(event.target.value)
                            }}
                          >
                            {getPeldaños(undefined).map((peldaño) => (
                              <MenuItem key={`peldaño-${peldaño}`} value={peldaño}>
                                {peldaño}
                              </MenuItem>
                            ))}
                          </Select>
                        </ThemeProvider>
                      </FormControl>
                    </Grid>
                  </>
                ) : productSelected.id === 1003 ? null : (
                  // Normal
                  <Grid item xs={12} className={classes.fullWidth}>
                    <FormControl fullWidth>
                      <ThemeProvider theme={themeCharofil}>
                        <InputLabel id="acabado-select-label">{displayAcabadosLabel()}</InputLabel>
                        <Select
                          fullWidth
                          labelId="acabado-select-label"
                          id="acabado-select"
                          key="acabado-select"
                          label={displayAcabadosLabel()}
                          value={productSelected.acabado ? `${productSelected.acabado.id}` : '0'}
                          onChange={(event: SelectChangeEvent) => {
                            handleAcabadoChange(parseInt(event.target.value), undefined)
                          }}
                        >
                          {productSelected
                            ? productSelected.acabados
                              ? productSelected.acabados.map((acabado) => (
                                  <MenuItem key={`acabado-${acabado.id}`} value={acabado.id}>
                                    {acabado.name}
                                  </MenuItem>
                                ))
                              : ''
                            : ''}
                        </Select>
                      </ThemeProvider>
                    </FormControl>
                  </Grid>
                )}

                {productSelected?.acabado?.relatedIds && productSelected?.acabadoRelated ? (
                  <Grid item xs={12} alignItems={'center'}>
                    <SwitchText
                      title="Tipo"
                      value={getBimetalicoType()}
                      text1="Interior"
                      value1="EZ"
                      text2="Exterior"
                      value2="GAC"
                      onChange={(value: string) => {
                        handleAcabadoChange(productSelected.acabado?.id, value)
                      }}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </div>
            <br />
            <br />
            <Typography
              variant="body1"
              color="textSecondary"
              component="p"
              style={
                productSelected.id === 1003
                  ? { textAlign: 'justify', paddingLeft: 20, paddingRight: 20 }
                  : {}
              }
            >
              {productSelected.acabado?.description}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item>
        <div className={classes.separator}></div>
      </Grid>
      <Grid container className={classes.cardContainer} alignItems="center">
        <Grid item xs={11} sx={{ display: { xs: 'block', md: 'none', backgroundColor: 'white' } }}>
          <Content />
        </Grid>
        <Grid item md={9} sx={{ display: { xs: 'none', md: 'block', backgroundColor: 'white' } }}>
          <Content />
        </Grid>
      </Grid>
      <Grid item>
        <div className={classes.separator}></div>
        <div className={classes.separator}></div>
      </Grid>

      <Grid className={classes.expandGrid2} />
      <Grid style={{ paddingBottom: 50 }}>
        <Button
          type="submit"
          variant="contained"
          onClick={handleOpenAddToList}
          style={{ backgroundColor: '#BE0A15' }}
        >
          {isMyListUpdate(indexEdit, myList) ? 'Actualizar' : 'Agregar mis productos a la lista'}
        </Button>
      </Grid>
      <Grid style={{ paddingTop: 10, paddingBottom: 25 }}>
        <Dialog
          fullWidth
          maxWidth="sm"
          open={open}
          onClose={handleCancelAddToList}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{dialogText.title}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {dialogText.description}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {dialogText.cancel ? (
              <ThemeProvider theme={themeCharofil}>
                <Button onClick={handleCancelAddToList} color="primary">
                  {dialogText.cancel}
                </Button>
              </ThemeProvider>
            ) : (
              ''
            )}
            <ThemeProvider theme={themeCharofil}>
              <Button onClick={handleAgreeAddToList} color="primary" autoFocus>
                {dialogText.agree}
              </Button>
            </ThemeProvider>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  ) : (
    <Loading />
  )
}

export default withRouter(Product)
